/**
 * screens/PolicyInForce — Approver · "Policy in force"
 * ──────────────────────────────────────────────────────────────────────────
 * The four policy numbers, read-only, plus the note that explains what they
 * mean for the person reading them.
 *
 * An approver cannot edit these — that is Finance's screen — but they need to
 * see them, because the two ends of the range determine what does and does not
 * reach their queue. Without this screen, "why did I never see that spend?" has
 * no answer they can check for themselves.
 * ──────────────────────────────────────────────────────────────────────────
 */
import { useCallback } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { Card, Eyebrow, Loading, PageContainer, PageHeader } from '../../components';
import { neutral } from '../../theme/colors';
import { sans, size as fontSize, leading } from '../../theme/typography';
import { useAsyncData } from '../../lib/useAsyncData';
import { fetchPolicyInForce } from '../../services/approvals/approvals.service';
import { useShell } from '../../context/ShellContext';
import PolicyQuad from '../Settings/components/PolicyQuad';

export function PolicyInForceScreen() {
  const { dataVersion } = useShell();

  const load = useCallback(() => fetchPolicyInForce(), []);
  const { data, loading } = useAsyncData(load, [], {
    version: dataVersion,
    errorTitle: 'Could not load the policy',
  });

  if (loading && !data) {
    return (
      <PageContainer width="approver">
        <Loading />
      </PageContainer>
    );
  }

  return (
    <PageContainer width="approver" testID="screen-policy">
      <PageHeader
        title="Policy in force"
        subtitle="The thresholds that decide what reaches you."
      />

      <Card>
        <PolicyQuad
          baseCurrency={data?.policy.base_currency ?? null}
          thresholdMinor={data?.policy.threshold_minor ?? null}
          autoUnderMinor={data?.policy.auto_under_minor ?? null}
          dualOverMinor={data?.policy.dual_over_minor ?? null}
        />
      </Card>

      {/*
        The API returns the RULES these thresholds seeded, so the screen shows
        them. The design specified only the quad, but an approver asking "why did
        this reach me?" is answered by the rule that caught it — not by four
        numbers they have to reason about. Each line is the API's own sentence.
      */}
      {!!data?.rules.length && (
        <View style={styles.rules}>
          <Eyebrow>Rules in force</Eyebrow>
          {data.rules
            .filter((rule) => rule.is_active)
            .map((rule) => (
              <View key={rule.id} style={styles.ruleRow}>
                <Text style={styles.ruleText}>
                  {rule.summary ?? rule.on_field}
                </Text>
                <Text style={styles.ruleApprovers}>{rule.approvers.join(' + ')}</Text>
              </View>
            ))}
        </View>
      )}

      <View style={styles.note}>
        <Text style={styles.noteText}>
          A spend under the auto-approve floor never reaches you. Above the dual-approval ceiling,
          a second approver is required after you.
        </Text>
      </View>
    </PageContainer>
  );
}

const styles = StyleSheet.create({
  rules: { marginTop: 18, gap: 8 },
  ruleRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: 12,
    flexWrap: 'wrap',
  },
  ruleText: { ...sans('400'), fontSize: fontSize.button, color: neutral.ink, flexShrink: 1 },
  ruleApprovers: { ...sans('500'), fontSize: fontSize.meta, color: neutral.grey600 },
  note: { marginTop: 14 },
  noteText: {
    ...sans('400'),
    fontSize: fontSize.button,
    lineHeight: leading(fontSize.button),
    color: neutral.grey600,
    maxWidth: 620,
  },
});

export default PolicyInForceScreen;
